import {
  BackSide,
  CircleGeometry,
  Color,
  CubeCamera,
  HalfFloatType,
  Mesh,
  MeshBasicMaterial,
  PMREMGenerator,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
  WebGLCubeRenderTarget,
} from 'three';
import type { Texture, WebGLRenderer, WebGLRenderTarget } from 'three';
import { LightProbeGenerator } from 'three/examples/jsm/lights/LightProbeGenerator.js';
import type { MapDef } from '@cloudpearl/config';
import { SKY_FS, SKY_VS } from '@cloudpearl/assets';
import type { SharedUniforms } from '@cloudpearl/assets';

/**
 * "Procedural HDRI": renders the map's own sky (sun, glow, cloud band) and a ground-bounce disc
 * into a small HDR cube map, then
 *  - projects it to 9 SH coefficients → the diffuse ambient every world shader uses, and
 *  - prefilters it (PMREM) → the environment map for physically based surfaces (the pearl).
 * So lighting always matches the sky you see, with no downloads.
 */
export class EnvLighting {
  private envRT: WebGLRenderTarget | null = null;

  constructor(private readonly gl: WebGLRenderer) {}

  get envMap(): Texture | null {
    return this.envRT?.texture ?? null;
  }

  async capture(shared: SharedUniforms, map: MapDef, blend = 0.7): Promise<void> {
    const scene = new Scene();
    const skyMat = new ShaderMaterial({
      uniforms: { ...shared.all(), uCloudBand: { value: map.clouds.skyBand }, uSunDisk: { value: 0 } },
      vertexShader: SKY_VS,
      fragmentShader: SKY_FS,
      side: BackSide,
      depthWrite: false,
    });
    const sky = new Mesh(new SphereGeometry(500, 48, 24), skyMat);
    scene.add(sky);
    // Ground bounce: the palette's ground ambient stands in for sunlit terrain below the horizon.
    // Sky Isles float over a sunlit cloud sea — a bright bounce from below lights the undersides.
    const below = map.terrain.islands.enabled ? new Color(map.palette.skyBottom).multiplyScalar(0.75) : new Color(map.palette.ambientGround).multiplyScalar(0.9);
    const groundMat = new MeshBasicMaterial({ color: below });
    const ground = new Mesh(new CircleGeometry(400, 48), groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = map.terrain.style === 'dune' ? -40 : -120;
    scene.add(ground);

    const cubeRT = new WebGLCubeRenderTarget(32, { type: HalfFloatType, generateMipmaps: false });
    const cam = new CubeCamera(0.5, 1000, cubeRT);
    cam.position.set(0, 0, 0);
    const prevTarget = this.gl.getRenderTarget();
    cam.update(this.gl, scene);

    // Diffuse SH from the capture, blended with the palette's hand-tuned ambient for art control.
    try {
      const probe = await LightProbeGenerator.fromCubeRenderTarget(this.gl, cubeRT);
      const captured = probe.sh.coefficients;
      const palette = shared.uSH.value.map((v) => v.clone());
      const out: Vector3[] = [];
      for (let i = 0; i < 9; i++) out.push(new Vector3().copy(palette[i]!).multiplyScalar(1 - blend).addScaledVector(captured[i]!, blend));
      shared.setSH(out);
    } catch (e) {
      console.warn('[cloudpearl] SH capture failed; keeping palette ambient', e);
    }

    const pmrem = new PMREMGenerator(this.gl);
    this.envRT?.dispose();
    this.envRT = pmrem.fromCubemap(cubeRT.texture);
    pmrem.dispose();
    this.gl.setRenderTarget(prevTarget);

    cubeRT.dispose();
    sky.geometry.dispose();
    skyMat.dispose();
    ground.geometry.dispose();
    groundMat.dispose();
  }

  dispose(): void {
    this.envRT?.dispose();
    this.envRT = null;
  }
}
